import * as React from 'react';
import { DatePicker } from 'Src/components/DatePicker/DatePicker';
import { useCloseOnOutside } from 'Src/hooks/useCloseOnOutside';
import { SltPeriods } from 'Src/models/slt';
import { comparisonUnavailableReason } from './ComparisonAvailability';
const day = (value: Date) =>
  value
    ? `${value.getFullYear()}-${('0' + (value.getMonth() + 1)).slice(-2)}-${('0' + value.getDate()).slice(-2)}`
    : '';
const date = (value: string) =>
  value ? new Date(Number(value.slice(0, 4)), Number(value.slice(5, 7)) - 1, Number(value.slice(8, 10))) : null;
export function InsightsToolbar({
  views,
  formatPercent = value => `${Math.round(value)}%`,
  params,
  periods,
  archives,
  data,
  comparison,
  onChange,
  onHow,
  clubsReport,
  openPeriodSignal = 0
}: {
  views?: React.ReactNode;
  formatPercent?: (value: number) => string;
  params: URLSearchParams;
  periods: SltPeriods;
  archives: any[];
  data: any;
  comparison: any;
  onChange: (changes: any) => void;
  onHow: () => void;
  clubsReport?: any;
  openPeriodSignal?: number;
}) {
  const [open, setOpen] = React.useState(''),
    [characteristicSearch, setCharacteristicSearch] = React.useState(''),
    [calendarYear, setYear] = React.useState(''),
    [customFrom, setFrom] = React.useState(params.get('from') || ''),
    [customTo, setTo] = React.useState(params.get('to') || '');
  const wrap = React.useRef<HTMLDivElement>(null);
  React.useEffect(() => {
    if (openPeriodSignal) setOpen('period');
  }, [openPeriodSignal]);
  useCloseOnOutside({ isOpen: !!open, onClose: () => setOpen(''), wrapRef: wrap });
  React.useEffect(() => {
    setFrom(params.get('from') || '');
    setTo(params.get('to') || '');
  }, [params.get('from'), params.get('to')]);
  React.useEffect(() => {
    if (open !== 'period' || !customFrom || !customTo || customFrom > customTo || customTo > periods.today) return;
    if (customFrom === params.get('from') && customTo === params.get('to')) return;
    const timer = window.setTimeout(
      () => onChange({ from: customFrom, to: customTo, period: null, snapshotId: null, page: 1 }),
      300
    );
    return () => window.clearTimeout(timer);
  }, [customFrom, customTo, open]);
  const years = [...(periods.calendar?.years || [])].sort((a, b) => a.start.localeCompare(b.start)),
    chosen = years.find(
      year =>
        year.id ===
        (calendarYear ||
          years.find(item => item.start <= periods.today && item.end >= periods.today)?.id ||
          years[years.length - 1]?.id)
    );
  const yearComparisonReason = comparisonUnavailableReason(periods, params.get('from'), params.get('to'), 'year');
  const termComparisonReason = comparisonUnavailableReason(periods, params.get('from'), params.get('to'), 'term');
  const compare = params.get('compare') || 'none',
    population = data?.population || params.get('population') || 'current';
  const selection = (changes: any) => {
    onChange({ ...(compare !== 'none' && (changes.from || changes.to) ? {population:'current',archiveAt:null} : {}), ...changes, snapshotId: null, page: 1 });
    setOpen('');
  };
  const toggle = (name: string) => {
    setCharacteristicSearch('');
    setOpen(open === name ? '' : name);
  };
  const options = [
    { value: '', label: 'All students' },
    { value: 'pupilsPremium:yes', label: 'Pupil Premium' },
    { value: 'freeSchoolMeals:yes', label: 'Free School Meals' },
    { value: 'specialEducationNeeds:yes', label: 'SEND' },
    { value: 'gender:FEMALE', label: 'Girls' },
    { value: 'gender:MALE', label: 'Boys' },
    ...(data?.filters.ethnicities || []).map(value => ({
      value: `ethnicityCode:${value}`,
      label: `Ethnicity: ${value}`
    })),
    ...(data?.filters.forms || []).map(value => ({ value: `form:${value}`, label: `Form: ${value}` })),
    ...(data?.filters.houses || []).map(value => ({ value: `house:${value}`, label: `House: ${value}` }))
  ];
  const characteristic = params.get('characteristic') || '',
    characteristic2 = params.get('characteristic2') || '',
    selectedYears = (params.get('years') || '').split(',').filter(Boolean);
  const searchWords = characteristicSearch.trim().toLocaleLowerCase('en-GB').split(/\s+/).filter(Boolean);
  const matchingOptions = options.filter(option => {
    const aliases = { 'pupilsPremium:yes': 'PP', 'freeSchoolMeals:yes': 'FSM',
      'specialEducationNeeds:yes': 'SEN SEND special educational needs' }[option.value] || '';
    const text = `${option.label} ${option.value} ${aliases}`.toLocaleLowerCase('en-GB');
    return searchWords.every(word => text.includes(word));
  });
  const range = params.get('from')
    ? `${new Date(params.get('from') + 'T00:00:00Z')
        .toLocaleDateString('en-GB', {
          day: 'numeric',
          month: 'short',
          timeZone: 'UTC'
        })
        .replace('Sept', 'Sep')} – ${new Date(params.get('to') + 'T00:00:00Z')
        .toLocaleDateString('en-GB', {
          day: 'numeric',
          month: 'short',
          timeZone: 'UTC'
        })
        .replace('Sept', 'Sep')}`
    : 'Choose period';
  return (
    <div className="sli-toolbar" ref={wrap}>
      {views}
      <span className="sli-toolbarDivider" />
      <div className="sli-toolbarControls">
        <button
          type="button"
          className="sli-select"
          aria-expanded={open === 'period'}
          onClick={() => toggle('period')}
          aria-label="Reporting period"
        >
          {(params.get('period') &&
            periods.presets
              .find(preset => preset.id === params.get('period'))
              ?.label.replace('Current academic year', 'This academic year')
              .replace('Past academic year', 'Last academic year')) ||
            range}
          {params.get('period') && params.get('from') && <em>{range}</em>}
          <span>⌄</span>
        </button>
        <button
          type="button"
          className="sli-select"
          aria-expanded={open === 'compare'}
          onClick={() => toggle('compare')}
          aria-label="Compare periods"
          title={comparison?.unavailableReason || (comparison?.cohortLabels
            ? `Selected: ${comparison.cohortLabels.primary}. Comparison: ${comparison.cohortLabels.comparison}`
            : 'Compare the actual school roster for each period')}
        >
          Compare <em>{compare === 'year' ? 'same point last year' : compare === 'term' ? 'previous term' : 'none'}</em>
          <span>⌄</span>
        </button>
        <span className="sli-toolbarDivider" aria-hidden="true" />
        <button type="button" className="sli-select" aria-expanded={open === 'years'} onClick={() => toggle('years')}>
          {selectedYears.length
            ? selectedYears.map(year => data?.filters.years.find(item => item.key === year)?.label || year).join(', ')
            : 'All year groups'}
          <span>⌄</span>
        </button>
        <button
          type="button"
          className="sli-select"
          aria-expanded={open === 'characteristic'}
          onClick={() => toggle('characteristic')}
        >
          {options.find(option => option.value === characteristic)?.label || 'All students'}
          <span>⌄</span>
        </button>
        <button
          type="button"
          className="sli-select sli-addCharacteristic"
          aria-expanded={open === 'characteristic2'}
          onClick={() => toggle('characteristic2')}
        >
          {characteristic2 ? options.find(option => option.value === characteristic2)?.label : '+ Characteristic'}
        </button>
      </div>
      <div className="sli-status">
        <span>
          <i className="sli-statusDot" />
          Updated{' '}
          {data
            ? new Date(data.sourceCutoffAt).toLocaleString('en-GB', {
                day: 'numeric',
                month: 'short',
                hour: '2-digit',
                minute: '2-digit',
                timeZone: data.timezone
              })
            : '…'}
        </span>
        {clubsReport && !clubsReport.totalSessions ? (
          <span>No club sessions in this period</span>
        ) : clubsReport && !clubsReport.endedSessions ? (
          <span>Registers not due yet</span>
        ) : (clubsReport ? clubsReport.coverage : data?.coverage.value) == null ? (
          <span>Register coverage unavailable</span>
        ) : (
          <>
            {' '}
            <span title="Proxy: ended sessions with explicit attendance marks. Register submission itself is not recorded.">
              <i
                className={`sli-statusDot ${
                  (clubsReport ? clubsReport.coverage : data?.coverage.value) == null ||
                  (clubsReport ? clubsReport.coverage : data.coverage.value) < 50
                    ? 'sli-red'
                    : (clubsReport ? clubsReport.coverage : data.coverage.value) <
                      (clubsReport?.thresholds.coverageTarget || 90)
                    ? 'sli-amber'
                    : ''
                }`}
              />
              Registers cover{' '}
              <strong>
                {(clubsReport ? clubsReport.coverage : data?.coverage.value) == null
                  ? '—'
                  : formatPercent(clubsReport ? clubsReport.coverage : data.coverage.value)}
              </strong>{' '}
              of sessions
            </span>
          </>
        )}
        <button type="button" className="sli-link" onClick={onHow}>
          How we count
        </button>
      </div>
      {open && (
        <div
          className={'sli-popover' + (open === 'characteristic' || open === 'characteristic2' ? ' sli-characteristicPopover' : '')}
          role="dialog"
          aria-label={open === 'period' ? 'Choose period' : open === 'compare' ? 'Choose comparison' : 'Choose filters'}
        >
          <div className="sli-popoverTitle">
            <strong>
              {open === 'period' ? 'Reporting period' : open === 'compare' ? 'Compare with' : 'Filter students'}
            </strong>
            <button type="button" className="sli-link" onClick={() => setOpen('')}>
              Close
            </button>
          </div>
          {open === 'period' && (
            <>
              <div className="sli-periodPresets">
                {periods.presets.map(preset => (
                  <button
                    type="button"
                    key={preset.id}
                    disabled={!!preset.disabledReason}
                    title={preset.disabledReason || preset.description}
                    onClick={() => {
                      if (preset.from && preset.to) selection({ from: preset.from, to: preset.to, period: preset.id });
                    }}
                  >
                    {preset.label}
                    {preset.disabledReason && <small>{preset.disabledReason}</small>}
                  </button>
                ))}
              </div>
              {years.length > 0 && (
                <>
                  <div className="sli-calendarYear">
                    <button
                      type="button"
                      className="sli-link"
                      disabled={!chosen || years.indexOf(chosen) <= 0}
                      onClick={() => setYear(years[years.indexOf(chosen) - 1].id)}
                      aria-label="Previous academic year"
                    >
                      ←
                    </button>
                    <strong>{chosen?.name}</strong>
                    <button
                      type="button"
                      className="sli-link"
                      disabled={!chosen || years.indexOf(chosen) >= years.length - 1}
                      onClick={() => setYear(years[years.indexOf(chosen) + 1].id)}
                      aria-label="Next academic year"
                    >
                      →
                    </button>
                  </div>
                  <div className="sli-termOptions">
                    {chosen?.terms.map(term => (
                      <button
                        type="button"
                        key={term.id}
                        disabled={term.start > periods.today}
                        onClick={() =>
                          selection({
                            from: term.start,
                            to: term.end > periods.today ? periods.today : term.end,
                            period: null
                          })
                        }
                      >
                        <strong>{term.name}</strong>
                        <small>
                          {term.start} – {term.end}
                        </small>
                      </button>
                    ))}
                  </div>
                </>
              )}
              <div className="sli-customRange">
                <DatePicker
                  label="From"
                  format="DD/MM/YYYY"
                  value={date(customFrom)}
                  onChange={value => setFrom(day(value as Date))}
                  clearable={false}
                  max={date(periods.today)}
                />
                <DatePicker
                  label="To"
                  format="DD/MM/YYYY"
                  value={date(customTo)}
                  onChange={value => setTo(day(value as Date))}
                  clearable={false}
                  min={date(customFrom)}
                  max={date(periods.today)}
                />
              </div>
              <p className="sli-help">Valid custom dates apply automatically. Future dates are unavailable.</p>
              <details className="sli-rosterOption">
                <summary>
                  Roster:{' '}
                  {compare !== 'none' ? 'school roster for each period' : population === 'archive'
                    ? 'on a recorded date'
                    : population === 'historical'
                    ? 'verified historical enrolment'
                    : 'current students'}
                </summary>
                {compare === 'none' ? <label>
                  Who is included?
                  <select
                    aria-label="Who is included?"
                    value={population}
                    onChange={event => {
                      const value = event.target.value;
                      selection({ population: value, archiveAt: value === 'archive' ? archives[0]?.observedAt : null });
                    }}
                  >
                    <option value="current">Current students</option>
                    <option value="historical">Verified historical enrolment</option>
                    <option value="archive" disabled={!archives.length}>
                      Roster on a recorded date…
                    </option>
                  </select>
                </label> : <p className="sli-help">
                  Each period uses its own verified membership history or a roster observed inside that period.
                  {comparison?.cohortLabels && <> Selected: {comparison.cohortLabels.primary}. Comparison: {comparison.cohortLabels.comparison}.</>}
                  {' '}Monthly observations do not establish daily membership between snapshots.
                </p>}
                {data?.participationPolicy && (
                  <span
                    className="sli-sourceBadge"
                    title="Club and event registration count towards participation; attendance is shown separately."
                  >
                    Participation: enrolment · Attendance: separate
                  </span>
                )}
                {compare === 'none' && population === 'archive' && (
                  <label>
                    Observation date
                    <select
                      value={data?.archive?.observedAt || params.get('archiveAt') || ''}
                      onChange={event => selection({ archiveAt: event.target.value, population: 'archive' })}
                    >
                      {archives.map(item => (
                        <option key={item.observedAt} value={item.observedAt}>
                          {new Date(item.observedAt).toLocaleDateString('en-GB')} · {item.students ?? 'Hidden'} students
                        </option>
                      ))}
                    </select>
                  </label>
                )}
                <p className="sli-help">
                  A monthly roster is a fixed cohort, not proof of enrolment on every day between observations.
                </p>
              </details>
            </>
          )}
          {open === 'compare' && (
            <div className="sli-periodPresets">
              <p className="sli-help">Compare each period's actual school roster and year groups.
                {comparison?.cohortLabels && <> Selected: {comparison.cohortLabels.primary}. Comparison: {comparison.cohortLabels.comparison}.</>}
              </p>
              <button
                type="button"
                disabled={!!yearComparisonReason}
                title={yearComparisonReason || ''}
                onClick={() => selection({ compare: 'year' })}
              >
                Same point last year{yearComparisonReason && <small>{yearComparisonReason}</small>}
              </button>
              <button
                type="button"
                disabled={!!termComparisonReason}
                title={termComparisonReason || ''}
                onClick={() => selection({ compare: 'term' })}
              >
                Previous term
              </button>
              <button type="button" onClick={() => selection({ compare: null })}>
                None
              </button>
            </div>
          )}
          {open === 'years' && (
            <div className="sli-options">
              <button
                type="button"
                className={!selectedYears.length ? 'on' : ''}
                onClick={() => onChange({ years: null, page: 1 })}
              >
                All year groups
              </button>
              {(data?.filters.years || []).map(year => (
                <label className="sli-check" key={year.key}>
                  <input
                    type="checkbox"
                    checked={selectedYears.includes(year.key)}
                    onChange={() =>
                      onChange({
                        years:
                          (selectedYears.includes(year.key)
                            ? selectedYears.filter(key => key !== year.key)
                            : [...selectedYears, year.key]
                          ).join(',') || null,
                        page: 1
                      })
                    }
                  />
                  {year.label}
                </label>
              ))}
            </div>
          )}
          {(open === 'characteristic' || open === 'characteristic2') && (
            <>
            <input
              type="search"
              className="sli-characteristicSearch"
              aria-label="Search student filters"
              placeholder="Search characteristics, forms or houses…"
              value={characteristicSearch}
              onChange={event => setCharacteristicSearch(event.target.value)}
              autoFocus
            />
            <div className="sli-options">
              {matchingOptions.map(option => (
                <button
                  type="button"
                  key={option.value}
                  className={params.get(open) === option.value ? 'on' : ''}
                  disabled={option.value.startsWith('gender:') && data && !data.availability.historicalGender}
                  onClick={() => selection({ [open]: option.value || null })}
                >
                  {option.label}
                </button>
              ))}
              {!matchingOptions.length && <p className="sli-help" role="status">No matching filters. Try another search.</p>}
            </div>
            </>
          )}
        </div>
      )}
    </div>
  );
}
